<template>
  <div v-if="instruction" class="tokeva-instruction-viewer">
    <!-- Header / Title -->
    <v-card outlined class="mb-4">
      <v-card-title
        class="d-flex align-center justify-space-between flex-wrap py-2"
      >
        <div class="d-flex align-center">
          <v-chip color="error" class="mr-2 font-weight-bold" small>
            {{ instruction.ohje }}
          </v-chip>
          <span class="text-h6 font-weight-bold">{{ mainTitle }}</span>
        </div>
        <div class="d-flex align-center flex-wrap my-1">
          <!-- View mode toggle: Tabs vs. Full List -->
          <v-btn-toggle v-model="viewMode" mandatory dense class="mr-2">
            <v-btn value="tabs" small title="Näytä välilehtinä (kuten TOKEVA)">
              <v-icon small left>mdi-tab</v-icon>
              Välilehdet
            </v-btn>
            <v-btn value="all" small title="Näytä kaikki osiot allekkain">
              <v-icon small left>mdi-view-headline</v-icon>
              Kaikki osiot
            </v-btn>
          </v-btn-toggle>

          <v-btn
            v-if="pdfUrl"
            :href="pdfUrl"
            target="_blank"
            small
            outlined
            color="primary"
            class="mr-2"
          >
            <v-icon left small>mdi-file-pdf-box</v-icon>
            Virallinen PDF
          </v-btn>
          <v-btn
            v-if="tokevaWebUrl"
            :href="tokevaWebUrl"
            target="_blank"
            small
            text
            color="secondary"
          >
            <v-icon left small>mdi-open-in-new</v-icon>
            Tokeva.fi
          </v-btn>
        </div>
      </v-card-title>

      <!-- Situations Tabs (e.g. Syttymätön vuoto vs. Palava vuoto) -->
      <v-tabs
        v-if="instruction.tilanteet && instruction.tilanteet.length > 1"
        v-model="activeSituationIndex"
        background-color="grey lighten-4"
        dense
        show-arrows
      >
        <v-tab
          v-for="(tilanne, idx) in instruction.tilanteet"
          :key="idx"
          class="font-weight-medium text-capitalize"
        >
          {{ tilanne.otsikko ? tilanne.otsikko.teksti : `Tilanne ${idx + 1}` }}
        </v-tab>
      </v-tabs>

      <!-- SECTION TABS (When in 'tabs' view mode) -->
      <v-tabs
        v-if="viewMode === 'tabs'"
        v-model="activeSectionTabIndex"
        background-color="grey lighten-3"
        dense
        show-arrows
        class="section-tabs"
      >
        <v-tab
          v-for="(tabItem, tIdx) in sectionTabs"
          :key="tIdx"
          class="font-weight-bold"
          :class="tabItem.isDangerZone ? 'error--text' : ''"
        >
          <v-icon
            small
            left
            :color="tabItem.isDangerZone ? 'error' : 'primary'"
          >
            {{ tabItem.icon }}
          </v-icon>
          {{ tabItem.title }}
        </v-tab>
      </v-tabs>
    </v-card>

    <!-- TAB-BASED VIEW MODE -->
    <div v-if="viewMode === 'tabs'">
      <!-- Active Section Tab Content -->
      <div v-if="currentTabItem">
        <!-- DANGER ZONE TAB -->
        <div v-if="currentTabItem.isDangerZone">
          <tokeva-danger-zone
            :small-zone="smallZone"
            :large-zone="largeZone"
            :chemical-name="chemicalName"
            :un-code="unCode"
            :t-instruction="instruction.ohje"
          />
        </div>

        <!-- REGULAR SECTION CONTENT -->
        <div v-else-if="currentTabItem.section">
          <v-card
            outlined
            :class="getSectionClass(currentTabItem.section.osio?.teksti)"
          >
            <v-card-title
              class="py-2 text-subtitle-1 font-weight-bold d-flex align-center"
            >
              <v-icon
                left
                :color="
                  getSectionIconColor(currentTabItem.section.osio?.teksti)
                "
              >
                {{ getSectionIcon(currentTabItem.section.osio?.teksti) }}
              </v-icon>
              {{ currentTabItem.section.osio?.teksti || "Ohjeistus" }}
            </v-card-title>
            <v-divider />
            <v-card-text class="py-3">
              <!-- Pelastusmuodostelma roles -->
              <div v-if="isOrganizationSection(currentTabItem.section)">
                <div
                  v-if="
                    extractOrganizationRoles(currentTabItem.section).length > 0
                  "
                >
                  <div
                    class="d-flex align-center justify-space-between mb-3 flex-wrap"
                  >
                    <div
                      class="text-subtitle-2 font-weight-bold grey--text text--darken-2"
                    >
                      Tehtäväjako ja vastuut:
                    </div>
                    <v-btn
                      x-small
                      outlined
                      color="primary"
                      @click="showFormationDiagram = !showFormationDiagram"
                    >
                      <v-icon left x-small>{{
                        showFormationDiagram ? "mdi-eye-off" : "mdi-sitemap"
                      }}</v-icon>
                      {{
                        showFormationDiagram
                          ? "Piilota muodostelmakaavio"
                          : "Näytä muodostelmakaavio"
                      }}
                    </v-btn>
                  </div>

                  <!-- Formation SVG Diagram -->
                  <v-expand-transition>
                    <div
                      v-if="showFormationDiagram"
                      class="text-center my-3 pa-3 grey lighten-4 rounded formation-diagram-box"
                    >
                      <img
                        src="/img/tokeva/pelastusmuodostelma.svg"
                        alt="Pelastusmuodostelmakaavio"
                        style="max-width: 280px; height: auto"
                      />
                      <div class="caption grey--text text--darken-1 mt-1">
                        Perusoperaatiomallin mukainen pelastusmuodostelma
                      </div>
                    </div>
                  </v-expand-transition>

                  <v-row dense>
                    <v-col
                      v-for="(role, roleIdx) in extractOrganizationRoles(
                        currentTabItem.section,
                      )"
                      :key="roleIdx"
                      cols="12"
                      md="6"
                    >
                      <v-sheet
                        outlined
                        rounded
                        class="pa-3 fill-height role-card"
                        :class="getRoleClass(role.title)"
                        :color="getRoleBgColor(role.title)"
                      >
                        <div
                          class="font-weight-bold text-subtitle-2 mb-2 d-flex align-center"
                        >
                          <v-icon small left color="primary"
                            >mdi-shield-account</v-icon
                          >
                          {{ role.title }}
                        </div>
                        <ul class="pl-4 mb-0">
                          <li
                            v-for="(task, tIdx) in role.tasks"
                            :key="tIdx"
                            class="body-2 mb-1"
                          >
                            <tokeva-rich-text
                              :content="task"
                              @open-gas-guide="$emit('open-gas-guide')"
                            />
                          </li>
                        </ul>
                      </v-sheet>
                    </v-col>
                  </v-row>
                </div>

                <!-- Non-role content items in organization section -->
                <div
                  v-if="
                    getNonRoleContentItems(currentTabItem.section).length > 0
                  "
                  class="mt-4"
                >
                  <div
                    v-for="(contentItem, cIdx) in getNonRoleContentItems(
                      currentTabItem.section,
                    )"
                    :key="'non-role-tab-' + cIdx"
                    class="content-block mb-3"
                  >
                    <div
                      v-if="contentItem.otsikko"
                      class="font-weight-bold text-subtitle-2 mb-1 primary--text"
                    >
                      {{ contentItem.otsikko.teksti }}
                    </div>
                    <div v-if="contentItem.teksti" class="body-2 mb-2">
                      <tokeva-rich-text
                        :content="contentItem.teksti"
                        @open-gas-guide="$emit('open-gas-guide')"
                      />
                    </div>
                    <div
                      v-if="contentItem.tyyliTeksti"
                      :class="getStyleTextClass(contentItem)"
                    >
                      <tokeva-rich-text
                        :content="contentItem.tyyliTeksti"
                        @open-gas-guide="$emit('open-gas-guide')"
                      />
                    </div>
                    <!-- Table Rendering -->
                    <tokeva-table
                      v-if="contentItem.taulukko"
                      :table-id="contentItem.taulukko"
                      @open-gas-guide="$emit('open-gas-guide')"
                      @select-un="$emit('select-un', $event)"
                    />
                    <ul
                      v-if="
                        contentItem.merkkiLista &&
                        contentItem.merkkiLista.luettelo
                      "
                      class="pl-4 mb-2 tokeva-bullet-list"
                    >
                      <li
                        v-for="(listItem, lIdx) in contentItem.merkkiLista
                          .luettelo"
                        :key="lIdx"
                        :class="
                          isSoleLink(listItem)
                            ? 'list-item-clean-action'
                            : 'body-2 mb-1'
                        "
                      >
                        <tokeva-rich-text
                          :content="listItem"
                          @open-gas-guide="$emit('open-gas-guide')"
                        />
                      </li>
                    </ul>
                    <div
                      v-if="
                        contentItem.numeroLista &&
                        contentItem.numeroLista.luettelo
                      "
                    >
                      <ol class="pl-4 mb-2">
                        <li
                          v-for="(numItem, nIdx) in contentItem.numeroLista
                            .luettelo"
                          :key="nIdx"
                          class="body-2 mb-1"
                        >
                          <tokeva-rich-text
                            :content="numItem"
                            @open-gas-guide="$emit('open-gas-guide')"
                          />
                        </li>
                      </ol>
                    </div>
                    <div
                      v-if="contentItem.osoite || contentItem.linkkiTeksti"
                      class="mt-1"
                    >
                      <v-btn
                        v-if="contentItem.osoite"
                        :href="resolveContentItemUrl(contentItem.osoite)"
                        target="_blank"
                        x-small
                        text
                        color="primary"
                      >
                        <v-icon left x-small>mdi-link</v-icon>
                        {{ contentItem.linkkiTeksti || contentItem.osoite }}
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>

              <!-- General section content -->
              <div v-else>
                <div
                  v-for="(contentItem, cIdx) in currentTabItem.section.sisalto"
                  :key="cIdx"
                  class="content-block mb-3"
                >
                  <!-- Section sub-header -->
                  <div
                    v-if="contentItem.otsikko"
                    class="font-weight-bold text-subtitle-2 mb-1 primary--text"
                  >
                    {{ contentItem.otsikko.teksti }}
                  </div>

                  <!-- Text items -->
                  <div v-if="contentItem.teksti" class="body-2 mb-2">
                    <tokeva-rich-text
                      :content="contentItem.teksti"
                      @open-gas-guide="$emit('open-gas-guide')"
                    />
                  </div>
                  <div
                    v-if="contentItem.tyyliTeksti"
                    :class="getStyleTextClass(contentItem)"
                  >
                    <tokeva-rich-text
                      :content="contentItem.tyyliTeksti"
                      @open-gas-guide="$emit('open-gas-guide')"
                    />
                  </div>
                  <!-- Table Rendering -->
                  <tokeva-table
                    v-if="contentItem.taulukko"
                    :table-id="contentItem.taulukko"
                    @open-gas-guide="$emit('open-gas-guide')"
                    @select-un="$emit('select-un', $event)"
                  />

                  <!-- Bullet List (Merkkilista, e.g. in Välineet, Vaaratekijät) -->
                  <ul
                    v-if="
                      contentItem.merkkiLista &&
                      contentItem.merkkiLista.luettelo
                    "
                    class="pl-4 mb-2 tokeva-bullet-list"
                  >
                    <li
                      v-for="(listItem, lIdx) in contentItem.merkkiLista
                        .luettelo"
                      :key="lIdx"
                      :class="
                        isSoleLink(listItem)
                          ? 'list-item-clean-action'
                          : 'body-2 mb-1'
                      "
                    >
                      <tokeva-rich-text
                        :content="listItem"
                        @open-gas-guide="$emit('open-gas-guide')"
                      />
                    </li>
                  </ul>

                  <!-- Numbered List (Toimenpiteet clean step boxes or regular numbered list) -->
                  <div
                    v-if="
                      contentItem.numeroLista &&
                      contentItem.numeroLista.luettelo
                    "
                  >
                    <!-- TOIMENPITEET: Dedicated, separated clean step boxes like in Tokeva.fi -->
                    <div
                      v-if="isToimenpiteetSection(currentTabItem.section)"
                      class="toimenpiteet-steps-container my-2"
                    >
                      <div
                        v-for="(stepItem, sIdx) in contentItem.numeroLista
                          .luettelo"
                        :key="sIdx"
                        :class="getStepBoxClass(stepItem)"
                      >
                        <div class="d-flex align-start">
                          <!-- Note warning icon -->
                          <v-icon
                            v-if="isNoteItem(stepItem)"
                            small
                            color="amber darken-3"
                            class="mr-2 mt-1 flex-shrink-0"
                          >
                            mdi-alert-circle-outline
                          </v-icon>

                          <!-- Step text with inline clickable M-ohjeet and links -->
                          <div class="step-text-wrapper flex-grow-1">
                            <tokeva-rich-text
                              :content="stepItem"
                              :format-number="!isNoteItem(stepItem)"
                              @open-gas-guide="$emit('open-gas-guide')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Regular numbered list for other sections -->
                    <ol v-else class="pl-4 mb-2">
                      <li
                        v-for="(numItem, nIdx) in contentItem.numeroLista
                          .luettelo"
                        :key="nIdx"
                        class="body-2 mb-1"
                      >
                        <tokeva-rich-text
                          :content="numItem"
                          @open-gas-guide="$emit('open-gas-guide')"
                        />
                      </li>
                    </ol>
                  </div>

                  <!-- Columns (Sarakkeet) -->
                  <div v-if="contentItem.sarakkeet" class="sarakkeet-block">
                    <v-row dense>
                      <v-col
                        v-for="(col, colIdx) in contentItem.sarakkeet"
                        :key="colIdx"
                        cols="12"
                        :md="contentItem.sarakkeet.length > 1 ? 6 : 12"
                      >
                        <div
                          v-for="(subItem, sIdx) in col"
                          :key="sIdx"
                          class="mb-2"
                        >
                          <div
                            v-if="subItem.otsikko"
                            class="font-weight-bold text-subtitle-2 mb-1"
                          >
                            {{ subItem.otsikko.teksti }}
                          </div>
                          <ul
                            v-if="
                              subItem.merkkiLista &&
                              subItem.merkkiLista.luettelo
                            "
                            class="pl-4 mb-1 tokeva-bullet-list"
                          >
                            <li
                              v-for="(cItem, ciIdx) in subItem.merkkiLista
                                .luettelo"
                              :key="ciIdx"
                              :class="
                                isSoleLink(cItem)
                                  ? 'list-item-clean-action'
                                  : 'body-2 mb-1'
                              "
                            >
                              <tokeva-rich-text
                                :content="cItem"
                                @open-gas-guide="$emit('open-gas-guide')"
                              />
                            </li>
                          </ul>
                          <div v-if="subItem.teksti" class="body-2 mb-1">
                            <tokeva-rich-text
                              :content="subItem.teksti"
                              @open-gas-guide="$emit('open-gas-guide')"
                            />
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Explicit Link or Document Button -->
                  <div
                    v-if="contentItem.osoite || contentItem.linkkiTeksti"
                    class="mt-1"
                  >
                    <v-btn
                      v-if="contentItem.osoite"
                      :href="resolveContentItemUrl(contentItem.osoite)"
                      target="_blank"
                      x-small
                      text
                      color="primary"
                    >
                      <v-icon left x-small>mdi-link</v-icon>
                      {{ contentItem.linkkiTeksti || contentItem.osoite }}
                    </v-btn>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </div>
    </div>

    <!-- ALL SECTIONS VERTICAL SCROLL MODE -->
    <div v-else-if="currentSituation" class="situation-content">
      <!-- 1. VAARA-ALUE & TILANNEKARTTA: Placed high on the page for immediate tactical overview -->
      <div class="mb-4">
        <tokeva-danger-zone
          :small-zone="smallZone"
          :large-zone="largeZone"
          :chemical-name="chemicalName"
          :un-code="unCode"
          :t-instruction="instruction.ohje"
        />
      </div>

      <div
        v-for="(ryhma, rIdx) in currentSituation.ohjeetJaTiedot"
        :key="rIdx"
        class="mb-4"
      >
        <div v-for="(osioItem, oIdx) in ryhma" :key="oIdx" class="mb-3">
          <v-card outlined :class="getSectionClass(osioItem.osio?.teksti)">
            <!-- Section Header -->
            <v-card-title
              class="py-2 text-subtitle-1 font-weight-bold d-flex align-center"
            >
              <v-icon left :color="getSectionIconColor(osioItem.osio?.teksti)">
                {{ getSectionIcon(osioItem.osio?.teksti) }}
              </v-icon>
              {{ osioItem.osio?.teksti || "Ohjeistus" }}
            </v-card-title>

            <v-divider />

            <!-- Section Body -->
            <v-card-text class="py-3">
              <!-- Special Layout: Pelastusmuodostelma (Organization Roles) -->
              <div v-if="isOrganizationSection(osioItem)">
                <div v-if="extractOrganizationRoles(osioItem).length > 0">
                  <div
                    class="d-flex align-center justify-space-between mb-3 flex-wrap"
                  >
                    <div
                      class="text-subtitle-2 font-weight-bold grey--text text--darken-2"
                    >
                      Tehtäväjako ja vastuut:
                    </div>
                    <v-btn
                      x-small
                      outlined
                      color="primary"
                      @click="showFormationDiagram = !showFormationDiagram"
                    >
                      <v-icon left x-small>{{
                        showFormationDiagram ? "mdi-eye-off" : "mdi-sitemap"
                      }}</v-icon>
                      {{
                        showFormationDiagram
                          ? "Piilota muodostelmakaavio"
                          : "Näytä muodostelmakaavio"
                      }}
                    </v-btn>
                  </div>

                  <!-- Formation SVG Diagram -->
                  <v-expand-transition>
                    <div
                      v-if="showFormationDiagram"
                      class="text-center my-3 pa-3 grey lighten-4 rounded formation-diagram-box"
                    >
                      <img
                        src="/img/tokeva/pelastusmuodostelma.svg"
                        alt="Pelastusmuodostelmakaavio"
                        style="max-width: 280px; height: auto"
                      />
                      <div class="caption grey--text text--darken-1 mt-1">
                        Perusoperaatiomallin mukainen pelastusmuodostelma
                      </div>
                    </div>
                  </v-expand-transition>

                  <v-row dense>
                    <v-col
                      v-for="(role, roleIdx) in extractOrganizationRoles(
                        osioItem,
                      )"
                      :key="roleIdx"
                      cols="12"
                      md="6"
                    >
                      <v-sheet
                        outlined
                        rounded
                        class="pa-3 fill-height role-card"
                        :class="getRoleClass(role.title)"
                        :color="getRoleBgColor(role.title)"
                      >
                        <div
                          class="font-weight-bold text-subtitle-2 mb-2 d-flex align-center"
                        >
                          <v-icon small left color="primary"
                            >mdi-shield-account</v-icon
                          >
                          {{ role.title }}
                        </div>
                        <ul class="pl-4 mb-0">
                          <li
                            v-for="(task, tIdx) in role.tasks"
                            :key="tIdx"
                            class="body-2 mb-1"
                          >
                            <tokeva-rich-text
                              :content="task"
                              @open-gas-guide="$emit('open-gas-guide')"
                            />
                          </li>
                        </ul>
                      </v-sheet>
                    </v-col>
                  </v-row>
                </div>

                <!-- Non-role content items in organization section -->
                <div
                  v-if="getNonRoleContentItems(osioItem).length > 0"
                  class="mt-4"
                >
                  <div
                    v-for="(contentItem, cIdx) in getNonRoleContentItems(
                      osioItem,
                    )"
                    :key="'non-role-vert-' + cIdx"
                    class="content-block mb-3"
                  >
                    <div
                      v-if="contentItem.otsikko"
                      class="font-weight-bold text-subtitle-2 mb-1 primary--text"
                    >
                      {{ contentItem.otsikko.teksti }}
                    </div>
                    <div v-if="contentItem.teksti" class="body-2 mb-2">
                      <tokeva-rich-text
                        :content="contentItem.teksti"
                        @open-gas-guide="$emit('open-gas-guide')"
                      />
                    </div>
                    <div
                      v-if="contentItem.tyyliTeksti"
                      :class="getStyleTextClass(contentItem)"
                    >
                      <tokeva-rich-text
                        :content="contentItem.tyyliTeksti"
                        @open-gas-guide="$emit('open-gas-guide')"
                      />
                    </div>
                    <!-- Table Rendering -->
                    <tokeva-table
                      v-if="contentItem.taulukko"
                      :table-id="contentItem.taulukko"
                      @open-gas-guide="$emit('open-gas-guide')"
                      @select-un="$emit('select-un', $event)"
                    />
                    <ul
                      v-if="
                        contentItem.merkkiLista &&
                        contentItem.merkkiLista.luettelo
                      "
                      class="pl-4 mb-2 tokeva-bullet-list"
                    >
                      <li
                        v-for="(listItem, lIdx) in contentItem.merkkiLista
                          .luettelo"
                        :key="lIdx"
                        :class="
                          isSoleLink(listItem)
                            ? 'list-item-clean-action'
                            : 'body-2 mb-1'
                        "
                      >
                        <tokeva-rich-text
                          :content="listItem"
                          @open-gas-guide="$emit('open-gas-guide')"
                        />
                      </li>
                    </ul>
                    <div
                      v-if="
                        contentItem.numeroLista &&
                        contentItem.numeroLista.luettelo
                      "
                    >
                      <ol class="pl-4 mb-2">
                        <li
                          v-for="(numItem, nIdx) in contentItem.numeroLista
                            .luettelo"
                          :key="nIdx"
                          class="body-2 mb-1"
                        >
                          <tokeva-rich-text
                            :content="numItem"
                            @open-gas-guide="$emit('open-gas-guide')"
                          />
                        </li>
                      </ol>
                    </div>
                    <div
                      v-if="contentItem.osoite || contentItem.linkkiTeksti"
                      class="mt-1"
                    >
                      <v-btn
                        v-if="contentItem.osoite"
                        :href="resolveContentItemUrl(contentItem.osoite)"
                        target="_blank"
                        x-small
                        text
                        color="primary"
                      >
                        <v-icon left x-small>mdi-link</v-icon>
                        {{ contentItem.linkkiTeksti || contentItem.osoite }}
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>

              <!-- General Content Items -->
              <div v-else>
                <div
                  v-for="(contentItem, cIdx) in osioItem.sisalto"
                  :key="cIdx"
                  class="content-block mb-3"
                >
                  <!-- Subheader -->
                  <div
                    v-if="contentItem.otsikko"
                    class="font-weight-bold text-subtitle-2 mb-1 primary--text"
                  >
                    {{ contentItem.otsikko.teksti }}
                  </div>

                  <!-- Text -->
                  <div v-if="contentItem.teksti" class="body-2 mb-2">
                    <tokeva-rich-text
                      :content="contentItem.teksti"
                      @open-gas-guide="$emit('open-gas-guide')"
                    />
                  </div>
                  <div
                    v-if="contentItem.tyyliTeksti"
                    :class="getStyleTextClass(contentItem)"
                  >
                    <tokeva-rich-text
                      :content="contentItem.tyyliTeksti"
                      @open-gas-guide="$emit('open-gas-guide')"
                    />
                  </div>
                  <!-- Table Rendering -->
                  <tokeva-table
                    v-if="contentItem.taulukko"
                    :table-id="contentItem.taulukko"
                    @open-gas-guide="$emit('open-gas-guide')"
                    @select-un="$emit('select-un', $event)"
                  />

                  <!-- Bullet List -->
                  <ul
                    v-if="
                      contentItem.merkkiLista &&
                      contentItem.merkkiLista.luettelo
                    "
                    class="pl-4 mb-2 tokeva-bullet-list"
                  >
                    <li
                      v-for="(listItem, lIdx) in contentItem.merkkiLista
                        .luettelo"
                      :key="lIdx"
                      :class="
                        isSoleLink(listItem)
                          ? 'list-item-clean-action'
                          : 'body-2 mb-1'
                      "
                    >
                      <tokeva-rich-text
                        :content="listItem"
                        @open-gas-guide="$emit('open-gas-guide')"
                      />
                    </li>
                  </ul>

                  <!-- Numbered List (Toimenpiteet clean step boxes or regular numbered list) -->
                  <div
                    v-if="
                      contentItem.numeroLista &&
                      contentItem.numeroLista.luettelo
                    "
                  >
                    <!-- TOIMENPITEET: Dedicated, separated clean step boxes like in Tokeva.fi -->
                    <div
                      v-if="isToimenpiteetSection(osioItem)"
                      class="toimenpiteet-steps-container my-2"
                    >
                      <div
                        v-for="(stepItem, sIdx) in contentItem.numeroLista
                          .luettelo"
                        :key="sIdx"
                        :class="getStepBoxClass(stepItem)"
                      >
                        <div class="d-flex align-start">
                          <!-- Note warning icon -->
                          <v-icon
                            v-if="isNoteItem(stepItem)"
                            small
                            color="amber darken-3"
                            class="mr-2 mt-1 flex-shrink-0"
                          >
                            mdi-alert-circle-outline
                          </v-icon>

                          <!-- Step text with inline clickable M-ohjeet and links -->
                          <div class="step-text-wrapper flex-grow-1">
                            <tokeva-rich-text
                              :content="stepItem"
                              :format-number="!isNoteItem(stepItem)"
                              @open-gas-guide="$emit('open-gas-guide')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Regular numbered list for other sections -->
                    <ol v-else class="pl-4 mb-2">
                      <li
                        v-for="(numItem, nIdx) in contentItem.numeroLista
                          .luettelo"
                        :key="nIdx"
                        class="body-2 mb-1"
                      >
                        <tokeva-rich-text
                          :content="numItem"
                          @open-gas-guide="$emit('open-gas-guide')"
                        />
                      </li>
                    </ol>
                  </div>

                  <!-- Columns -->
                  <div v-if="contentItem.sarakkeet" class="sarakkeet-block">
                    <v-row dense>
                      <v-col
                        v-for="(col, colIdx) in contentItem.sarakkeet"
                        :key="colIdx"
                        cols="12"
                        :md="contentItem.sarakkeet.length > 1 ? 6 : 12"
                      >
                        <div
                          v-for="(subItem, sIdx) in col"
                          :key="sIdx"
                          class="mb-2"
                        >
                          <div
                            v-if="subItem.otsikko"
                            class="font-weight-bold text-subtitle-2 mb-1"
                          >
                            {{ subItem.otsikko.teksti }}
                          </div>
                          <ul
                            v-if="
                              subItem.merkkiLista &&
                              subItem.merkkiLista.luettelo
                            "
                            class="pl-4 mb-1 tokeva-bullet-list"
                          >
                            <li
                              v-for="(cItem, ciIdx) in subItem.merkkiLista
                                .luettelo"
                              :key="ciIdx"
                              :class="
                                isSoleLink(cItem)
                                  ? 'list-item-clean-action'
                                  : 'body-2 mb-1'
                              "
                            >
                              <tokeva-rich-text
                                :content="cItem"
                                @open-gas-guide="$emit('open-gas-guide')"
                              />
                            </li>
                          </ul>
                          <div v-if="subItem.teksti" class="body-2 mb-1">
                            <tokeva-rich-text
                              :content="subItem.teksti"
                              @open-gas-guide="$emit('open-gas-guide')"
                            />
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- External Link -->
                  <div
                    v-if="contentItem.osoite || contentItem.linkkiTeksti"
                    class="mt-1"
                  >
                    <v-btn
                      v-if="contentItem.osoite"
                      :href="resolveContentItemUrl(contentItem.osoite)"
                      target="_blank"
                      x-small
                      text
                      color="primary"
                    >
                      <v-icon left x-small>mdi-link</v-icon>
                      {{ contentItem.linkkiTeksti || contentItem.osoite }}
                    </v-btn>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TokevaDangerZone from "@/components/TokevaDangerZone.vue";
import TokevaRichText from "@/components/TokevaRichText.vue";
import TokevaTable from "@/components/TokevaTable.vue";

export default {
  name: "TokevaInstructionViewer",
  components: {
    TokevaDangerZone,
    TokevaRichText,
    TokevaTable,
  },
  props: {
    instruction: {
      type: Object,
      default: null,
    },
    chemicalId: {
      type: String,
      default: "",
    },
    chemicalName: {
      type: String,
      default: "",
    },
    unCode: {
      type: String,
      default: "",
    },
    smallZone: {
      type: String,
      default: "25 / 50",
    },
    largeZone: {
      type: String,
      default: "50 / 100",
    },
  },
  data: () => ({
    viewMode: "all", // 'tabs' | 'all'
    activeSituationIndex: 0,
    activeSectionTabIndex: 0,
    showFormationDiagram: false,
  }),
  computed: {
    mainTitle() {
      if (!this.instruction) return "";
      const first = this.instruction.tilanteet?.[0]?.otsikko?.teksti;
      return first || `Toimintaohje ${this.instruction.ohje || ""}`;
    },
    currentSituation() {
      if (!this.instruction?.tilanteet) return null;
      return (
        this.instruction.tilanteet[this.activeSituationIndex] ||
        this.instruction.tilanteet[0]
      );
    },
    sectionTabs() {
      if (!this.currentSituation?.ohjeetJaTiedot) return [];
      const tabs = [];
      let hazardInserted = false;

      for (const ryhma of this.currentSituation.ohjeetJaTiedot) {
        for (const item of ryhma) {
          const title = item.osio?.teksti || "Ohjeistus";
          tabs.push({
            title,
            icon: this.getSectionIcon(title),
            section: item,
            isDangerZone: false,
          });

          // Insert VAARA-ALUE right after Vaaratekijät (matching tokeva.fi order)
          if (!hazardInserted && this.isHazardSection(item)) {
            tabs.push({
              title: "VAARA-ALUE",
              icon: "mdi-radius-outline",
              section: null,
              isDangerZone: true,
            });
            hazardInserted = true;
          }
        }
      }

      // If Vaaratekijät was not in the section list, append VAARA-ALUE
      if (!hazardInserted) {
        tabs.push({
          title: "VAARA-ALUE",
          icon: "mdi-radius-outline",
          section: null,
          isDangerZone: true,
        });
      }

      return tabs;
    },
    currentTabItem() {
      return (
        this.sectionTabs[this.activeSectionTabIndex] ||
        this.sectionTabs[0] ||
        null
      );
    },
    pdfUrl() {
      if (!this.instruction?.ohje) return null;
      const code = this.instruction.ohje.toLowerCase();
      const capCode =
        code === "cbrne"
          ? "Cbrne"
          : code.charAt(0).toUpperCase() + code.slice(1);
      return `https://tokeva.fi/assets/attachment-files/TOhjeet/Tokeva_${capCode}-ohje.pdf`;
    },
    tokevaWebUrl() {
      if (!this.instruction?.ohje) return null;
      const code = this.instruction.ohje.toLowerCase().replace(".", "");
      let url = `https://tokeva.fi/#/t-ohjeet/tohje/${code}`;
      if (this.chemicalId) {
        url += `;id=${encodeURIComponent(this.chemicalId)}`;
      }
      return url;
    },
  },
  watch: {
    instruction() {
      this.activeSituationIndex = 0;
      this.activeSectionTabIndex = 0;
    },
  },
  methods: {
    isToimenpiteetSection(osioItem) {
      const title = (osioItem?.osio?.teksti || "").toLowerCase();
      return title.includes("toimenpide") || title.includes("toimenpiteet");
    },
    isNoteItem(item) {
      if (!item) return false;
      if (item.tyyliteksti || item.tyyliTeksti) return true;
      let text = "";
      if (typeof item === "string") text = item;
      else if (item.teksti) text = item.teksti;
      else if (Array.isArray(item.kohtaSisalto)) {
        const firstTextPart = item.kohtaSisalto.find(
          (p) => typeof p === "string" || (p && p.teksti),
        );
        text =
          typeof firstTextPart === "string"
            ? firstTextPart
            : firstTextPart?.teksti || "";
      }
      return !/^\s*\d+\.\s*/.test(text);
    },
    isSoleLink(item) {
      if (!item) return false;
      if (item.linkkiTeksti || item.osoite) return true;
      if ((item.linkkiTag || item.linkkitag) && Object.keys(item).length === 1)
        return true;
      return false;
    },
    getStepBoxClass(stepItem) {
      if (this.isNoteItem(stepItem)) {
        return "toimenpide-note-box";
      }
      return "toimenpide-step-box";
    },
    resolveContentItemUrl(url) {
      if (!url) return "#";
      if (url.startsWith("http://") || url.startsWith("https://")) return url;
      if (url.startsWith("assets/") || url.startsWith("/assets/")) {
        return `https://tokeva.fi/${url.replace(/^\//, "")}`;
      }
      return `https://tokeva.fi/${url}`;
    },
    isHazardSection(osioItem) {
      const title = (osioItem?.osio?.teksti || "").toLowerCase();
      return title.includes("vaara") && !title.includes("alue");
    },
    isOrganizationSection(osioItem) {
      const title = (osioItem?.osio?.teksti || "").toLowerCase();
      return (
        title.includes("pelastusmuodostelma") || title.includes("organisointi")
      );
    },
    extractOrganizationRoles(osioItem) {
      const roles = [];
      const sisalto = osioItem?.sisalto || [];
      for (const s of sisalto) {
        if (Array.isArray(s.sarakkeet)) {
          for (const col of s.sarakkeet) {
            if (Array.isArray(col)) {
              for (let i = 0; i < col.length; i++) {
                const entry = col[i];
                // Case A: otsikko and merkkiLista are in the same object (e.g. T8A)
                if (entry.otsikko && entry.merkkiLista?.luettelo) {
                  roles.push({
                    title: entry.otsikko.teksti || entry.otsikko,
                    tasks: entry.merkkiLista.luettelo,
                  });
                }
                // Case B: otsikko and merkkiLista are consecutive items in the array (e.g. T1-T7, T8B-T9B, CBRNE)
                else if (entry.otsikko && col[i + 1]?.merkkiLista?.luettelo) {
                  roles.push({
                    title: entry.otsikko.teksti || entry.otsikko,
                    tasks: col[i + 1].merkkiLista.luettelo,
                  });
                  i++; // skip next since it is the merkkiLista
                }
              }
            }
          }
        }
      }
      return roles;
    },
    getNonRoleContentItems(osioItem) {
      if (!osioItem?.sisalto) return [];
      return osioItem.sisalto.filter((s) => !s.sarakkeet);
    },
    getStyleTextClass(item) {
      const tyylit = item?.tyylit || [];
      if (tyylit.includes("as-segment") || tyylit.includes("in-bold")) {
        return "text-subtitle-2 font-weight-bold grey--text text--darken-3 mt-3 mb-1";
      }
      if (tyylit.includes("in-italic")) {
        return "body-2 mb-2 font-italic";
      }
      return "body-2 mb-2 font-weight-medium";
    },
    getRoleClass(title) {
      const t = (title || "").toLowerCase();
      if (t.includes("johtaja")) return "role-sheet-leader";
      if (t.includes("i pelastus") || t.includes("1") || t.includes("i vastuu"))
        return "role-sheet-r1";
      if (
        t.includes("ii pelastus") ||
        t.includes("2") ||
        t.includes("ii vastuu")
      )
        return "role-sheet-r2";
      if (
        t.includes("iii pelastus") ||
        t.includes("3") ||
        t.includes("iii vastuu")
      )
        return "role-sheet-r3";
      return "";
    },
    getSectionClass(title) {
      const t = (title || "").toLowerCase();
      if (t.includes("vaara")) return "border-hazard";
      if (t.includes("suojain") || t.includes("henkilö"))
        return "border-protection";
      if (t.includes("väline")) return "border-tools";
      if (t.includes("toimenpide")) return "border-action";
      if (t.includes("muodostelma") || t.includes("organisointi"))
        return "border-organization";
      return "";
    },
    getSectionIcon(title) {
      const t = (title || "").toLowerCase();
      if (t.includes("muodostelma") || t.includes("organisointi"))
        return "mdi-account-group";
      if (t.includes("vaara")) return "mdi-alert-octagon";
      if (t.includes("suojain") || t.includes("henkilö"))
        return "mdi-shield-alert";
      if (t.includes("väline")) return "mdi-tools";
      if (t.includes("toimenpide")) return "mdi-fire-truck";
      if (t.includes("tiedustelu")) return "mdi-binoculars";
      return "mdi-clipboard-text";
    },
    getSectionIconColor(title) {
      const t = (title || "").toLowerCase();
      if (t.includes("vaara")) return "error";
      if (t.includes("suojain") || t.includes("henkilö"))
        return "blue darken-2";
      if (t.includes("väline")) return "amber darken-3";
      if (t.includes("toimenpide")) return "green darken-2";
      return "primary";
    },
    getRoleBgColor(title) {
      const t = (title || "").toLowerCase();
      if (t.includes("johtaja")) return "amber lighten-5";
      if (t.includes("i pelastus") || t.includes("1") || t.includes("i vastuu"))
        return "red lighten-5";
      if (
        t.includes("ii pelastus") ||
        t.includes("2") ||
        t.includes("ii vastuu")
      )
        return "blue lighten-5";
      if (
        t.includes("iii pelastus") ||
        t.includes("3") ||
        t.includes("iii vastuu")
      )
        return "green lighten-5";
      return "grey lighten-5";
    },
  },
};
</script>

<style scoped lang="scss">
.tokeva-instruction-viewer {
  .section-tabs {
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }

  .border-hazard {
    border-left: 5px solid #d32f2f !important;
  }
  .border-protection {
    border-left: 5px solid #1976d2 !important;
  }
  .border-tools {
    border-left: 5px solid #f57c00 !important;
  }
  .border-action {
    border-left: 5px solid #388e3c !important;
  }
  .border-organization {
    border-left: 5px solid #7b1fa2 !important;
  }

  .role-sheet-leader {
    border-left: 4px solid #f57f17 !important;
  }
  .role-sheet-r1 {
    border-left: 4px solid #d32f2f !important;
  }
  .role-sheet-r2 {
    border-left: 4px solid #1976d2 !important;
  }
  .role-sheet-r3 {
    border-left: 4px solid #388e3c !important;
  }
  .formation-diagram-box {
    border: 1px solid #e0e0e0;
  }

  /* TOIMENPITEET STEP BOXES */
  .toimenpiteet-steps-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .toimenpide-step-box {
    background-color: #f7f9fa;
    border: 1px solid #dcdcdc;
    border-radius: 4px;
    padding: 12px 14px;
    line-height: 1.55;
    font-size: 0.88rem;
    color: #212529;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
    transition: all 0.15s ease-in-out;

    &:hover {
      border-color: #b0bec5;
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
    }
  }

  .toimenpide-note-box {
    background-color: #fffde7;
    border: 1px solid #ffe082;
    border-left: 4px solid #ffb300;
    border-radius: 4px;
    padding: 10px 14px;
    line-height: 1.5;
    font-size: 0.88rem;
    color: #3e2723;
  }

  .list-item-clean-action {
    list-style-type: none;
    margin-left: -1rem;
    margin-top: 6px;
    margin-bottom: 6px;
  }
}

/* Theme dark adjustments */
.theme--dark {
  .tokeva-instruction-viewer {
    .toimenpide-step-box {
      background-color: #1e2227;
      border: 1px solid #363c45;
      color: #e6edf3;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);

      &:hover {
        border-color: #546e7a;
      }
    }

    .toimenpide-note-box {
      background-color: #2b2713;
      border: 1px solid #5d4a13;
      border-left: 4px solid #ffb300;
      color: #fff8e1;
    }

    .formation-diagram-box {
      background-color: #21252b !important;
      border-color: #363c45 !important;
    }

    .role-card {
      background-color: #1e2227 !important;
      border-color: #363c45 !important;
      color: #e6edf3 !important;
    }

    .role-sheet-leader {
      border-left: 4px solid #ffb300 !important;
    }
    .role-sheet-r1 {
      border-left: 4px solid #e53935 !important;
    }
    .role-sheet-r2 {
      border-left: 4px solid #1e88e5 !important;
    }
    .role-sheet-r3 {
      border-left: 4px solid #43a047 !important;
    }
  }
}
</style>
